import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Container, Typography, Card, CardContent, Button, CircularProgress, Alert, Grid, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import RequireAuth from '../../components/athletes/RequireAuth';
import { listMyAthletes, listArchivedAthletes, archiveAthlete, restoreAthlete } from '../../services/athleteService';

function age(dob) {
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  const diff = Date.now() - d.getTime();
  return Math.floor(diff / (365.25 * 24 * 60 * 60 * 1000));
}

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString();
}

/** Archive confirmation — a deliberate, explicit step before removing an
 * athlete from active use, since (unlike Restore) this changes what a
 * guardian sees and can act on day to day. Wording matches the approved
 * copy exactly: archiving is reversible and never touches history. */
function ArchiveConfirmDialog({ athlete, onClose, onConfirm, archiving }) {
  return (
    <Dialog open={Boolean(athlete)} onClose={() => !archiving && onClose()}>
      <DialogTitle>Archive {athlete?.first_name}?</DialogTitle>
      <DialogContent>
        <Typography sx={{ color: '#9eb8d0', fontSize: '0.9rem' }}>
          {athlete?.first_name} will be removed from active athlete lists and will no longer be available for new team
          activity. Historical records will be preserved and {athlete?.first_name} can be restored later.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={archiving} sx={{ textTransform: 'none', color: '#6a8aa8' }}>
          Cancel
        </Button>
        <Button onClick={onConfirm} disabled={archiving} variant="contained" color="secondary" sx={{ textTransform: 'none', fontWeight: 700 }}>
          {archiving ? <CircularProgress size={18} sx={{ color: '#050c16' }} /> : 'Archive Athlete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AthletesInner() {
  const navigate = useNavigate();
  const [athletes, setAthletes] = useState([]);
  const [archivedAthletes, setArchivedAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showArchived, setShowArchived] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(null); // athlete pending confirmation
  const [archiving, setArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState(null);
  const [restoringId, setRestoringId] = useState(null);
  const [restoreError, setRestoreError] = useState(null);

  const load = useCallback(async () => {
    const [active, archived] = await Promise.all([listMyAthletes(), listArchivedAthletes()]);
    setAthletes(active);
    setArchivedAthletes(archived);
  }, []);

  useEffect(() => {
    load().catch(e => setError(e.message)).finally(() => setLoading(false));
  }, [load]);

  const handleArchiveConfirmed = async () => {
    if (!confirmArchive) return;
    setArchiveError(null);
    setArchiving(true);
    try {
      await archiveAthlete(confirmArchive.id);
      setConfirmArchive(null);
      await load();
    } catch (e) {
      setArchiveError(e.message || 'Could not archive that athlete.');
    } finally {
      setArchiving(false);
    }
  };

  const handleRestore = async (athleteId) => {
    setRestoreError(null);
    setRestoringId(athleteId);
    try {
      await restoreAthlete(athleteId);
      await load();
    } catch (e) {
      setRestoreError(e.message || 'Could not restore that athlete.');
    } finally {
      setRestoringId(null);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 900 }}>My Athletes</Typography>
        {/* Only shown once there's a roster — an empty roster has one clear
            call to action (the centered empty-state button below), not two. */}
        {!loading && athletes.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Button variant="outlined" color="primary" onClick={() => navigate('/athletes/join-team')} sx={{ textTransform: 'none', fontWeight: 700 }}>
              Enter Team Join Code
            </Button>
            <Button variant="contained" color="primary" onClick={() => navigate('/athletes/new')} sx={{ textTransform: 'none', fontWeight: 700 }}>
              + Create Athlete
            </Button>
          </Box>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress sx={{ color: '#2ea84b' }} /></Box>
      ) : athletes.length === 0 ? (
        <Card sx={{ backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a' }}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography sx={{ fontSize: '2.2rem', mb: 1.5 }}>🏈</Typography>
            <Typography sx={{ fontWeight: 700, mb: 1 }}>No athletes yet</Typography>
            <Typography sx={{ color: '#9eb8d0', mb: 3 }}>Create your first athlete to start tracking testing and development.</Typography>
            <Button variant="contained" color="primary" onClick={() => navigate('/athletes/new')} sx={{ textTransform: 'none', fontWeight: 700 }}>
              + Create Athlete
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {athletes.map(a => (
            <Grid item xs={12} sm={6} key={a.id}>
              <Card
                onClick={() => navigate(`/athletes/${a.id}`)}
                sx={{ cursor: 'pointer', backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a', height: '100%', '&:hover': { borderColor: 'rgba(46,168,75,0.4)' } }}
              >
                <CardContent>
                  <Typography sx={{ fontWeight: 800, fontSize: '1.05rem' }}>{a.first_name} {a.last_name}</Typography>
                  <Box sx={{ display: 'flex', gap: 0.75, mt: 1, flexWrap: 'wrap' }}>
                    <Chip size="small" label={`Grade ${a.grade}`} sx={{ bgcolor: '#131318', color: '#9eb8d0' }} />
                    {age(a.date_of_birth) !== null && <Chip size="small" label={`Age ${age(a.date_of_birth)}`} sx={{ bgcolor: '#131318', color: '#9eb8d0' }} />}
                  </Box>
                  <Button
                    size="small" onClick={(e) => { e.stopPropagation(); setConfirmArchive(a); setArchiveError(null); }}
                    sx={{ mt: 1.5, textTransform: 'none', color: '#6a8aa8', p: 0, minWidth: 0, '&:hover': { color: '#f44336', background: 'none' } }}
                  >
                    Archive Athlete
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {archivedAthletes.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Button
            onClick={() => setShowArchived(v => !v)}
            sx={{ textTransform: 'none', color: '#6a8aa8', fontWeight: 700, p: 0, minWidth: 0 }}
          >
            {showArchived ? 'Hide' : 'View'} Archived Athletes ({archivedAthletes.length})
          </Button>

          {showArchived && (
            <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {restoreError && <Alert severity="error">{restoreError}</Alert>}
              {archivedAthletes.map(a => (
                <Card key={a.id} sx={{ backgroundColor: 'rgba(10,20,34,0.6)', border: '1px solid #1e2a3a', opacity: 0.75 }}>
                  <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                    <Box>
                      <Typography sx={{ fontWeight: 700 }}>{a.first_name} {a.last_name}</Typography>
                      <Typography sx={{ color: '#6a8aa8', fontSize: '0.78rem' }}>
                        Archived {a.archived_at ? formatDate(a.archived_at) : '—'}
                      </Typography>
                    </Box>
                    <Button
                      size="small" variant="outlined" color="primary" disabled={restoringId === a.id}
                      onClick={() => handleRestore(a.id)} sx={{ textTransform: 'none', fontWeight: 700 }}
                    >
                      {restoringId === a.id ? <CircularProgress size={14} /> : 'Restore Athlete'}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </Box>
      )}

      {archiveError && <Alert severity="error" sx={{ mt: 2 }}>{archiveError}</Alert>}

      <ArchiveConfirmDialog
        athlete={confirmArchive} archiving={archiving}
        onClose={() => setConfirmArchive(null)} onConfirm={handleArchiveConfirmed}
      />
    </Container>
  );
}

export default function MyAthletesPage() {
  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Navbar />
      <RequireAuth><AthletesInner /></RequireAuth>
    </Box>
  );
}
